import React, { ReactElement, FocusEvent, FC, useMemo } from 'react'
import { IMaskInput } from 'react-imask'
import classnames from 'classnames'
import { SmsCodeField } from './components/SmsCodeField/SmsCodeField'
import { getCountry, getValidationOptions, getMask, getCountryIcon } from './utils'
import { CallAndSmsCodeField } from './components/CallAndSmsCodeField/CallAndSmsCodeField'
import CheckIcon from './assets/CheckIcon'
import { CustomError } from '../CustomError/CustomError'
import { useEnv } from '../../utils/useEnv'
import style from './PhoneInput2.module.css'
import { useAppDispatch, useAppSelector } from '../../store'
import { setIsFocused } from '../../store/slices/focuseInputSlice'

type PhoneInput2Props = {
  label?: string | null | ReactElement
  name: string
  value: string
  codeFieldValue: string
  error?: string
  login: string
  codeError?: ReactElement | string | null
  isShowCodeInput?: boolean
  isCodeInputDisabled: boolean
  isPhoneBusy: boolean
  isPhoneSubmitted: boolean
  touched?: boolean
  onlySmsCode?: boolean
  isCallCode: boolean
  isShowCodeRegFormInput?: boolean
  eventGroup?: string
  shortForm?: boolean
  onChange?(phone: string): void
  onFocus?(data: FocusEvent<HTMLInputElement, Element>): void
  onBlur?(data: FocusEvent<HTMLInputElement, Element>): void
  onCodeFocus?(data: FocusEvent<HTMLInputElement, Element>): void
  onCodeChange?(data: FocusEvent<HTMLInputElement, Element>): void
  onCodeBlur?(data: FocusEvent<HTMLInputElement, Element>): void
  onCodeClick(success: () => void, fail?: () => void, isCall?: boolean): void
}

export const PhoneInput2: FC<PhoneInput2Props> = ({
  name,
  value,
  label,
  error,
  touched,
  codeError,
  isShowCodeInput,
  isCodeInputDisabled,
  codeFieldValue,
  isPhoneSubmitted,
  isPhoneBusy,
  login,
  onlySmsCode,
  isCallCode,
  isShowCodeRegFormInput,
  eventGroup,
  shortForm,
  onChange,
  onFocus,
  onBlur,
  onCodeChange,
  onCodeClick,
  onCodeFocus,
  onCodeBlur,
}) => {
  const dispatch = useAppDispatch()
  const { isFocused } = useAppSelector((state) => state.isFocused)
  const deployEnv = useEnv()
  const appropriateOptions = useMemo(() => getValidationOptions(value, deployEnv), [value, deployEnv])
  const mask = getMask(appropriateOptions)
  const country = useMemo(() => getCountry(appropriateOptions), [appropriateOptions])

  const inputClassNames = classnames(style.input, {
    [style.errorField]: !isFocused && touched && error,
    [style.checked]: isPhoneSubmitted,
  })

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    dispatch(setIsFocused(true))
    if (onFocus) onFocus(e)
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    dispatch(setIsFocused(false))
    if (onBlur) onBlur(e)
  }

  const showSmsInputOnReg1Form = isShowCodeRegFormInput && isShowCodeInput && !isPhoneSubmitted && onlySmsCode
  const showCallCodeInput = isShowCodeInput && !isPhoneSubmitted && !onlySmsCode
  const showCallCodeInputOnShortForm = !isShowCodeRegFormInput && isShowCodeInput && !isPhoneSubmitted && onlySmsCode

  return (
    <div className={style.phoneInput}>
      <label className={style.label}>{label}</label>
      <div className={style.phoneInputContent}>
        <div className={style.inputContainer}>
          <IMaskInput
            className={inputClassNames}
            name={name}
            mask={mask}
            value={value}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onAccept={onChange}
          />
          <div className={style.country}>{getCountryIcon(country)}</div>
          {isPhoneSubmitted && <CheckIcon className={style.checkIcon} />}
        </div>
        <CustomError
          error={!isFocused && touched && error ? error : undefined}
          isBusy={isPhoneBusy}
          login={login}
          touched={touched}
        />
        {showSmsInputOnReg1Form ? (
          <SmsCodeField
            className={style.smsCode}
            value={codeFieldValue}
            disabled={isCodeInputDisabled}
            error={codeError}
            isCallCode={isCallCode}
            onChange={onCodeChange}
            onBlur={onCodeBlur}
            onFocus={onCodeFocus}
            onClick={onCodeClick}
          />
        ) : null}
        {showCallCodeInput && (
          <CallAndSmsCodeField
            className={style.smsCode}
            value={codeFieldValue}
            disabled={isCodeInputDisabled}
            error={codeError}
            isCallCode={isCallCode}
            eventGroup={eventGroup}
            shortForm={shortForm}
            onChange={onCodeChange}
            onBlur={onCodeBlur}
            onFocus={onCodeFocus}
            onClick={onCodeClick}
          />
        )}
        {showCallCodeInputOnShortForm && (
          <SmsCodeField
            className={style.smsCode}
            value={codeFieldValue}
            disabled={isCodeInputDisabled}
            error={codeError}
            isCallCode={isCallCode}
            onChange={onCodeChange}
            onBlur={onCodeBlur}
            onFocus={onCodeFocus}
            onClick={onCodeClick}
          />
        )}
      </div>
    </div>
  )
}

PhoneInput2.displayName = 'PhoneInput2'
